// ═══════════════════════════════════════════════════════════════
// VACACIONES — Tab Historial
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { Search, Filter, ClipboardList, TrendingUp, TrendingDown } from 'lucide-react';

const YEAR_OPTIONS = [null, 2023, 2024, 2025, 2026, 2027];

const TRANSACTION_TYPE_MAP = {
  accrual:    { label: 'Asignación', bg: 'bg-green-100',  text: 'text-green-700'  },
  usage:      { label: 'Uso',        bg: 'bg-blue-100',   text: 'text-blue-700'   },
  adjustment: { label: 'Ajuste',     bg: 'bg-amber-100',  text: 'text-amber-700'  },
  payout:     { label: 'Pago',       bg: 'bg-purple-100', text: 'text-purple-700' },
};

const MAX_ROWS = 200;

export default function HistorialTab({ transactions = [], loading, onFilter, readOnly = false }) {
  const [searchEmployee, setSearchEmployee] = useState('');
  const [selectedYear, setSelectedYear]     = useState(null);

  // Notificar al padre cuando cambian los filtros (solo si no es readOnly)
  useEffect(() => {
    if (onFilter) onFilter({ employeeId: null, year: selectedYear });
    // eslint-disable-next-line
  }, [selectedYear]);

  // Filtrado local por nombre de empleado (sin round-trip al servidor)
  const filtered = transactions.filter(tx => {
    if (!searchEmployee.trim()) return true;
    const name = (tx.employee_name || '').toLowerCase();
    return name.includes(searchEmployee.toLowerCase());
  });

  const visible = filtered.slice(0, MAX_ROWS);
  const truncated = filtered.length > MAX_ROWS;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const s = String(dateStr).split('T')[0];
    return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Búsqueda por empleado — oculta en modo lectura (empleado solo ve los suyos) */}
        {!readOnly && (
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchEmployee}
              onChange={(e) => setSearchEmployee(e.target.value)}
              placeholder="Buscar empleado…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        )}

        {/* Selector de año */}
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-gray-400 shrink-0" />
          <select
            value={selectedYear ?? ''}
            onChange={(e) => setSelectedYear(e.target.value === '' ? null : parseInt(e.target.value, 10))}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white cursor-pointer"
          >
            <option value="">Todos los años</option>
            {YEAR_OPTIONS.filter(Boolean).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Contador */}
        <span className="text-sm text-gray-400 ml-auto">
          {filtered.length} {filtered.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {/* Tabla */}
      {loading ? (
        <LoadingState />
      ) : visible.length === 0 ? (
        <EmptyState hasFilters={!!searchEmployee || !!selectedYear} />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {truncated && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 font-medium">
              Mostrando las últimas {MAX_ROWS} transacciones de {filtered.length} totales.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">Fecha</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Empleado</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Días</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Referencia</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Notas</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">Registrado por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((tx, idx) => {
                  const typeInfo = TRANSACTION_TYPE_MAP[tx.transaction_type] || {
                    label: tx.transaction_type,
                    bg: 'bg-gray-100',
                    text: 'text-gray-700',
                  };
                  const days = parseFloat(tx.days ?? 0);
                  return (
                    <tr key={tx.id ?? idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(tx.transaction_date || tx.created_at)}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {tx.employee_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.bg} ${typeInfo.text}`}>
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 ${days > 0 ? 'text-green-600' : days < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {days > 0
                            ? <TrendingUp size={13} />
                            : days < 0
                              ? <TrendingDown size={13} />
                              : null
                          }
                          {days > 0 ? `+${days}` : days}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {tx.reference || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate">
                        {tx.notes || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {tx.created_by_name || tx.created_by || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <ClipboardList size={26} className="text-gray-400" />
      </div>
      <p className="text-gray-700 font-medium mb-1">
        {hasFilters ? 'Sin resultados para los filtros aplicados' : 'No hay transacciones registradas'}
      </p>
      {hasFilters && (
        <p className="text-sm text-gray-500">Intenta con otros criterios de búsqueda.</p>
      )}
    </div>
  );
}
